/**
 * `create_deal` / `update_deal` payload validation.
 *
 * Both return the INPUT OBJECT ITSELF, not a reconstructed one. That is load
 * bearing: `update-deal.handler` spreads the result over the current deal
 * (`{ ...currentDeal, ...updates }`), so rebuilding the object from the fields
 * this module knows about would silently drop everything it does not.
 *
 * Typing note (main-03): the parameter is `unknown` and the pass-through is an
 * assertion FUNCTION, not an `as` cast — `assertValidated` narrows the record in
 * place after the checks have run, so the return needs no cast at all.
 */

import { DEAL_STAGE_VALUES } from '../../types/deal-tool.types';
import type { CreateDealInput, UpdateDealInput } from '../../types/deal-tool.types';
import {
  checkAmount,
  checkBoolean,
  checkCloseDate,
  checkCloseProbability,
  checkCurrency,
  checkListFields,
  checkMonthlyAmount,
  checkNotes,
  checkStage,
  whenDefined,
  whenNotNullish,
  whenTruthy,
  type ErrorList,
} from './fields.js';

/**
 * Narrow raw tool input to a record. A non-object payload is a validation
 * failure like any other, reported through the same aggregated message.
 */
function toRecord(input: unknown, context: string): Record<string, unknown> {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    throw new Error(`${context} validation failed: input must be an object`);
  }
  return input as Record<string, unknown>;
}

/**
 * Assert the record satisfies `T` now that its fields have been checked. An
 * assertion function is how a validated `Record<string, unknown>` becomes the
 * declared shape WITHOUT an `as` at the return site, and it keeps the original
 * object identity (see the pass-through note above).
 */
function assertValidated<T>(
  record: Record<string, unknown>,
  errors: ErrorList,
  context: string,
): asserts record is Record<string, unknown> & T {
  if (errors.length > 0) {
    throw new Error(`${context} validation failed: ${errors.join('; ')}`);
  }
}

/** Validate deal creation input */
export function validateCreateDealInput(input: unknown): CreateDealInput {
  const record = toRecord(input, 'Deal creation');
  const errors: ErrorList = [];

  if (!record.company_id || typeof record.company_id !== 'string') {
    errors.push('company_id is required and must be a string');
  }

  if (
    !record.title ||
    typeof record.title !== 'string' ||
    record.title.trim().length === 0
  ) {
    errors.push('title is required and must be a non-empty string');
  }

  // amount is OPTIONAL: omitted when the user did not state one (the model must
  // never invent a placeholder like 1). An explicit null is treated as omitted.
  checkAmount(record.amount, whenNotNullish, errors);

  // Create gates these on TRUTHINESS — see ./fields for why that is a named
  // argument here instead of a different-looking `if`.
  checkCurrency(record.currency, whenTruthy, errors);
  checkStage(record.stage, whenTruthy, errors, DEAL_STAGE_VALUES);
  checkCloseDate(record.close_date, whenTruthy, errors);

  checkCloseProbability(record.close_probability, errors);
  checkBoolean(record.is_recurring, 'is_recurring', errors);
  checkMonthlyAmount(record.monthly_amount, errors);

  /**
   * NOT ENFORCED, ON PURPOSE — STILL NEEDS AN OWNER DECISION.
   *
   * There is no rule here requiring `monthly_amount` on a recurring deal, and
   * there never has been one that could fire. What stood here was
   *
   *     if (record.monthly_amount !== undefined) {
   *       if (record.is_recurring && record.monthly_amount === undefined) {
   *         errors.push('monthly_amount is required when is_recurring is true');
   *       }
   *     }
   *
   * — a rule whose condition the enclosing guard had already excluded, so it
   * never once ran. The unreachable `errors.push` is deleted (deleting code that
   * cannot execute changes nothing at runtime) precisely BECAUSE it read as
   * enforcement to everyone who scanned this function; leaving it there, with or
   * without a comment, is how a validator gets credited with a check it does not
   * perform.
   *
   * Restructuring so the impossible case is a compile error is not available:
   * TypeScript permits `x === undefined` for every type, including one narrowed
   * away from `undefined`, so no arrangement of these values produces a type
   * error. The compiler cannot carry this; a test has to.
   *
   * ENFORCING IT IS AN OWNER DECISION, NOT A REFACTOR'S. It would start
   * REJECTING `create_deal` calls that succeed today, on a MONEY field: today a
   * recurring deal may be created with no monthly amount, and Meridian creates
   * deals on a rep's behalf. The one-line change, when someone signs off, is
   *
   *     if (record.is_recurring && record.monthly_amount === undefined) {
   *       errors.push('monthly_amount is required when is_recurring is true');
   *     }
   *
   * placed HERE, outside any guard, plus the same in `validateUpdateDealInput`
   * if a patch that clears the amount should also be refused. Pinned by
   * `tests/lib/tools/deal-validation.test.ts` ("does NOT enforce monthly_amount
   * when is_recurring is true"), which asserts the current, unenforced
   * behaviour and must be updated in the same commit that enforces it.
   */

  checkBoolean(record.is_committed, 'is_committed', errors);
  checkListFields(record, errors);
  checkNotes(record.notes, errors);

  assertValidated<CreateDealInput>(record, errors, 'Deal creation');
  return record;
}

/** Validate deal update input */
export function validateUpdateDealInput(input: unknown): UpdateDealInput {
  const record = toRecord(input, 'Deal update');
  const errors: ErrorList = [];

  // A patch: every field is optional, but an explicit null is a real value the
  // caller sent, so update gates on PRESENCE throughout.
  if (record.title !== undefined) {
    if (typeof record.title !== 'string' || record.title.trim().length === 0) {
      errors.push('title must be a non-empty string');
    }
  }

  checkAmount(record.amount, whenDefined, errors);
  checkCurrency(record.currency, whenDefined, errors);
  checkStage(record.stage, whenDefined, errors, DEAL_STAGE_VALUES);
  checkCloseDate(record.close_date, whenDefined, errors);

  checkCloseProbability(record.close_probability, errors);
  checkBoolean(record.is_recurring, 'is_recurring', errors);
  checkMonthlyAmount(record.monthly_amount, errors);
  checkBoolean(record.is_committed, 'is_committed', errors);
  checkListFields(record, errors);
  checkNotes(record.notes, errors);

  assertValidated<UpdateDealInput>(record, errors, 'Deal update');
  return record;
}
